// =============================================================================
// src/features/explore/explore-ui.ts — Chrome da tela Explorar (chips/painel/card).
//
// Proveniencia: assets/explore.js `var ExploreUI = {...}` (linhas 400-653). Porte
//   FIEL: monta os 4 menus de chip, o painel de Camadas, liga a busca (que abre a
//   command palette global), o card de drill-in, o dismiss global (click/Escape) e
//   a legenda. Classes/atributos/ordem sao paridade — NAO alterar.
//
// Implementa ExploreMapUiPort (refresh/showCard/hideCard) para o mapa nao importar
//   a UI diretamente (DI; quebra o ciclo). Dados vem do mapa (ExploreMap) e de
//   src/data via os builders de card/menu — NUNCA do global DATA/EXPLORE_AM.
// =============================================================================

import { qs, qsa, on } from '../../app/dom';
import {
  EXPLORE_YEARS,
  EXPLORE_ZONES,
  PARTY_COLORS,
  PARTY_ORDER,
  RAMPS,
  METRIC_META,
  CAT_META,
  CAMPAIGN_MARKER_META,
  RESULTADO_METRICS,
  TERRITORIO_METRICS,
  CAMPANHA_METRICS,
  ESTRATEGICO_METRICS,
  THEME_METRICS,
  MICRO_LIST,
  ELEITORADO_BANDS,
  electionYearLabel,
  type ExploreColorMetric,
} from './constants';
import { REGIONS, EXPLORE_DATASET, MANAUS_ZONA_DISPLAY_NAME } from '../../data';
import type { AmazonasRegion, ElectoralYear, MunicipalityId, ExplorePoiCategory, ExploreCampaignMarkerKind, ZonaKey } from '../../app/types';
import {
  ExploreMap,
  type ExploreMapUiPort,
  type ExploreSelectionKind,
} from './explore-map';
import { buildMunCardHtml, buildZoneCardHtml } from './explore-card';
import {
  fillExploreMenu,
  buildPoliticalOption,
  buildPoiToggle,
  buildCampaignToggle,
  buildTseZoneToggle,
  buildG26LocaisToggle,
  resetToggleRow,
  buildVoteVizSegment,
  setChipText,
  toggleChipActive,
  markMenuChecked,
  type ExploreMenuItem,
} from './explore-menus';
import { VOTE_VIZ_MODES } from './explore-vote-constants';
import { rankedCandidates, candidateLabelOf, findCandidate } from './explore-vote-candidates';
import { getSearchPalette, getTopbarNav, getMapController, getExploreRouteGlobal } from './explore-globals';
import { showMiniHint } from './mini-hint';
import { G26_YEAR_VALUE, G26_YEAR_LABEL, G26_DEFAULT_CARGO } from './explore-g26-constants';
import type { G26CargoCode } from './explore-g26-types';
import {
  G26_YEAR_MENU_ITEM,
  g26CargoMenuItems,
  g26CandidateMenuItems,
  g26PartyMenuItems,
  g26CandidateChipLabel,
  g26CargoChipLabel,
  attachMenuSearch,
} from './explore-g26-ui';
import { buildG26MunCardHtml } from './explore-g26-card';

/** Ms ate o drill-in focar zona/municipio apos fechar a rota (assets/explore.js 547). */
const DRILL_IN_DELAY_MS = 90;

/** Par chip<->menu (id do botao e do menu) para wiring de abertura. */
type ChipMenuPair = readonly [chipId: string, menuId: string];

/** Rotulo curto da faixa de eleitorado para o chip (null quando sem recorte). */
function eleitoradoLabel(value: string | null): string | null {
  if (!value) return null;
  return ELEITORADO_BANDS.find((b) => b.value === value)?.label ?? value;
}

const CHIP_MENU_PAIRS: readonly ChipMenuPair[] = [
  ['expChipAno', 'expMenuAno'],
  ['expChipPartido', 'expMenuPartido'],
  ['expChipRegiao', 'expMenuRegiao'],
  ['expChipTema', 'expMenuTema'],
  // diverge do golden por decisao de produto 2026-05-31: recortes novos.
  ['expChipMicro', 'expMenuMicro'],
  ['expChipEleitorado', 'expMenuEleitorado'],
  ['expChipSegundo', 'expMenuSegundo'],
  // overlay de votos por candidato (estilo Datapedia).
  ['expChipCandidato', 'expMenuCandidato'],
  // cargo das Eleicoes 2026 (chip so aparece no modo 2026).
  ['expChipCargo', 'expMenuCargo'],
];

/** Menus de candidato com mais itens que isto ganham campo de busca. */
const MENU_SEARCH_MIN_ITEMS = 16;

/**
 * Controlador da UI da tela Explorar. Singleton exportado ao final (ExploreUI),
 * mesma forma de uso do `var ExploreUI` legado. Implementa ExploreMapUiPort.
 */
class ExploreScreenUi implements ExploreMapUiPort {
  private inited = false;
  private activeMenu: HTMLElement | null = null;
  private openMenuButton: HTMLElement | null = null;
  // Chave (ano historico ou cargo 2026) com que os menus dependentes foram montados.
  private dynamicMenusKey = '';

  /** Liga toda a UI uma unica vez (assets/explore.js 403-413). */
  init(): void {
    if (this.inited) return;
    this.inited = true;
    this.buildChipMenus();
    this.buildLayersPanel();
    this.buildVoteVizControl();
    this.wireLayersButton();
    this.wireSearch();
    this.wireCard();
    this.wireGlobalDismiss();
    this.refresh();
    // Abre na eleicao mais recente (Gerais 2026, TSE oficial) — pedido 2026-10-13.
    // Silencioso: se o asset falhar, o Explorar segue no ultimo ano historico.
    void ExploreMap.setElection2026(G26_DEFAULT_CARGO);
  }

  /** Entra no modo 2026 / troca de cargo a pedido do usuario; avisa se o asset falhar. */
  private enterG26(cargo: G26CargoCode): Promise<boolean> {
    return ExploreMap.setElection2026(cargo).then((ok) => {
      if (!ok) showMiniHint('Não foi possível carregar os resultados de 2026');
      return ok;
    });
  }

  // ── menus de chip ────────────────────────────────────────────────────────────

  /** Preenche os 4 menus de chip e liga a abertura de cada chip (assets/explore.js 416-440). */
  private buildChipMenus(): void {
    this.fillYearMenu();
    this.fillPartyMenu();
    this.fillRegionMenu();
    this.fillThemeMenu();
    this.fillMicroMenu();
    this.fillEleitoradoMenu();
    this.fillSegundoMenu();
    this.fillCandidateMenu();
    CHIP_MENU_PAIRS.forEach((pair) => this.wireChipToggle(pair));
  }

  /**
   * Menu de candidato + opcao "Agregado (vencedor)". Itens vem de rankedCandidates
   * (ordenados por votos), com swatch da cor do partido. Construido uma vez no ano
   * default; a colocacao/votos no rotulo refletem esse ano.
   */
  private fillCandidateMenu(): void {
    const menu = qs<HTMLElement>('#expMenuCandidato');
    if (!menu) return;
    const g26 = ExploreMap.g26.selection();
    const items: readonly ExploreMenuItem[] = g26 ? g26CandidateMenuItems(g26) : [
      { value: '', label: 'Agregado (vencedor)' },
      ...rankedCandidates(ExploreMap.year).map((r) => ({
        value: r.cand.id, label: candidateLabelOf(r), swatch: PARTY_COLORS[r.cand.party],
      })),
    ];
    fillExploreMenu(menu, items, (v) => { ExploreMap.setCandidate(v || null); this.closeMenu(); });
    if (items.length >= MENU_SEARCH_MIN_ITEMS) attachMenuSearch(menu, 'Buscar candidato ou partido');
  }

  /** Menu de Cargo do modo 2026 (vazio fora dele; o chip fica oculto). */
  private fillCargoMenu(): void {
    const menu = qs<HTMLElement>('#expMenuCargo');
    const g26 = ExploreMap.g26.selection();
    if (!menu || !g26) return;
    fillExploreMenu(menu, g26CargoMenuItems(g26.ds), (v) => { this.closeMenu(); void this.enterG26(v as G26CargoCode); });
  }

  /**
   * Remonta os menus que dependem do ano/cargo (partido, 2º, candidato, cargo) so
   * quando a chave muda — 2026 tem outras siglas e candidatos REAIS por cargo.
   */
  private refreshDynamicMenus(): void {
    const g26 = ExploreMap.g26.selection();
    const key = g26 ? `g26:${g26.cargo}` : `hist:${ExploreMap.year}`;
    if (key === this.dynamicMenusKey) return;
    this.dynamicMenusKey = key;
    this.fillPartyMenu();
    this.fillSegundoMenu();
    this.fillCandidateMenu();
    this.fillCargoMenu();
  }

  /** Preenche o seletor segmentado "Tipo de Visualização" (toggle do overlay de votos). */
  private buildVoteVizControl(): void {
    const host = qs<HTMLElement>('#expVizControl');
    if (!host) return;
    host.innerHTML = '';
    VOTE_VIZ_MODES.forEach((meta) => host.appendChild(
      buildVoteVizSegment(meta, (mode) => ExploreMap.setVoteViz(ExploreMap.voteViz === mode ? 'nenhum' : mode)),
    ));
  }

  /** Menu de microrregiao + opcao "Todas" (decisao de produto 2026-05-31). */
  private fillMicroMenu(): void {
    const menu = qs<HTMLElement>('#expMenuMicro');
    if (!menu) return;
    const items: ExploreMenuItem[] = [
      { value: '', label: 'Todas as microrregiões' },
      ...MICRO_LIST.map((m) => ({ value: m, label: m })),
    ];
    fillExploreMenu(menu, items, (v) => { ExploreMap.setMicro(v || null); this.closeMenu(); });
  }

  /** Menu de faixa de eleitorado + opcao "Todas" (decisao de produto 2026-05-31). */
  private fillEleitoradoMenu(): void {
    const menu = qs<HTMLElement>('#expMenuEleitorado');
    if (!menu) return;
    const items: ExploreMenuItem[] = [
      { value: '', label: 'Todas as faixas' },
      ...ELEITORADO_BANDS.map((b) => ({ value: b.value, label: b.label })),
    ];
    fillExploreMenu(menu, items, (v) => { ExploreMap.setEleitoradoBand(v || null); this.closeMenu(); });
  }

  /** Menu de 2º colocado (recorte) + opcao "Qualquer" (decisao de produto 2026-05-31). */
  private fillSegundoMenu(): void {
    const menu = qs<HTMLElement>('#expMenuSegundo');
    if (!menu) return;
    const g26 = ExploreMap.g26.selection();
    const items: readonly ExploreMenuItem[] = g26 ? g26PartyMenuItems(g26, 'Qualquer 2º colocado') : [
      { value: '', label: 'Qualquer 2º colocado' },
      ...PARTY_ORDER.map((p) => ({ value: p, label: p, swatch: PARTY_COLORS[p] })),
    ];
    fillExploreMenu(menu, items, (v) => { ExploreMap.setRunnerUp(v || null); this.closeMenu(); });
    menu.classList.toggle('exp-menu--long', items.length >= MENU_SEARCH_MIN_ITEMS);
  }

  /** Menu de ano (assets/explore.js 418). */
  private fillYearMenu(): void {
    const menu = qs<HTMLElement>('#expMenuAno');
    if (!menu) return;
    const items = [...EXPLORE_YEARS.map((y) => ({ value: y, label: electionYearLabel(y) })), G26_YEAR_MENU_ITEM];
    fillExploreMenu(menu, items, (v) => {
      this.closeMenu();
      if (v === G26_YEAR_VALUE) void this.enterG26(ExploreMap.g26.cargo);
      else ExploreMap.setYear(v as ElectoralYear);
    });
  }

  /** Menu de partido com swatch + opcao "Todos" (assets/explore.js 420-422). */
  private fillPartyMenu(): void {
    const menu = qs<HTMLElement>('#expMenuPartido');
    if (!menu) return;
    const g26 = ExploreMap.g26.selection();
    const items: readonly ExploreMenuItem[] = g26 ? g26PartyMenuItems(g26, 'Todos os partidos') : [
      { value: '', label: 'Todos os partidos' },
      ...PARTY_ORDER.map((p) => ({ value: p, label: p, swatch: PARTY_COLORS[p] })),
    ];
    fillExploreMenu(menu, items, (v) => { ExploreMap.setParty(v || null); this.closeMenu(); });
    menu.classList.toggle('exp-menu--long', items.length >= MENU_SEARCH_MIN_ITEMS);
  }

  /** Menu de regiao com opcao "Todo o Amazonas" (assets/explore.js 424-425). */
  private fillRegionMenu(): void {
    const menu = qs<HTMLElement>('#expMenuRegiao');
    if (!menu) return;
    const items: ExploreMenuItem[] = [
      { value: '', label: 'Todo o Amazonas' },
      ...REGIONS.map((r) => ({ value: r, label: r })),
    ];
    fillExploreMenu(menu, items, (v) => { ExploreMap.setRegion((v as AmazonasRegion) || null); this.closeMenu(); });
  }

  /**
   * Menu de tema (os 7 eixos socioeconomicos) + opcao "Nenhum (limpo)".
   * diverge do golden por decisao de produto 2026-05-31 (era "Nenhum (partido)").
   */
  private fillThemeMenu(): void {
    const menu = qs<HTMLElement>('#expMenuTema');
    if (!menu) return;
    const items: ExploreMenuItem[] = [
      { value: '', label: 'Nenhum (limpo)' },
      ...THEME_METRICS.map((k) => ({ value: k, label: METRIC_META[k]?.label ?? k, swatch: RAMPS[k]?.[3] })),
    ];
    fillExploreMenu(menu, items, (v) => { ExploreMap.setColorBy((v || 'nenhum') as ExploreColorMetric); this.closeMenu(); });
  }

  /** Liga o clique de um chip para abrir/fechar seu menu (assets/explore.js 432-440). */
  private wireChipToggle(pair: ChipMenuPair): void {
    const [chipId, menuId] = pair;
    const button = qs<HTMLElement>(`#${chipId}`);
    const menu = qs<HTMLElement>(`#${menuId}`);
    if (!button || !menu) return;
    on(button, 'click', (ev) => {
      ev.stopPropagation();
      if (this.activeMenu === menu) this.closeMenu();
      else { this.closeMenu(); this.openMenu(menu, button); }
    });
  }

  /** Abre um menu e marca aria-expanded no chip (assets/explore.js 454). */
  private openMenu(menu: HTMLElement, button: HTMLElement): void {
    menu.hidden = false;
    button.setAttribute('aria-expanded', 'true');
    this.activeMenu = menu;
    this.openMenuButton = button;
  }

  /** Fecha o menu aberto, se houver (assets/explore.js 455). */
  closeMenu(): void {
    if (!this.activeMenu) return;
    this.activeMenu.hidden = true;
    this.openMenuButton?.setAttribute('aria-expanded', 'false');
    this.activeMenu = null;
    this.openMenuButton = null;
  }

  // ── painel de camadas ────────────────────────────────────────────────────────

  /** Monta as opcoes politicas, toggles de POI/campanha e o toggle de acoes (assets/explore.js 457-491). */
  private buildLayersPanel(): void {
    this.buildPoliticalOptions();
    this.buildPoiToggles();
    this.buildCampaignToggles();
    this.buildTseZoneToggleRow();
    this.wireAcoesToggle();
  }

  /**
   * Botoes-radio das 3 frentes (Resultado/Território/Campanha) em 3 hosts.
   * diverge do golden por decisao de produto 2026-05-31 (era um grupo unico).
   */
  private buildPoliticalOptions(): void {
    this.fillOptionGroup('#expResultadoOptions', RESULTADO_METRICS);
    this.fillOptionGroup('#expTerritorioOptions', TERRITORIO_METRICS);
    this.fillOptionGroup('#expCampanhaOptions', CAMPANHA_METRICS);
    this.fillOptionGroup('#expEstrategicoOptions', ESTRATEGICO_METRICS);
  }

  /** Preenche um host de opcoes-radio com a lista de metricas dada. */
  private fillOptionGroup(selector: string, metrics: readonly ExploreColorMetric[]): void {
    const host = qs<HTMLElement>(selector);
    if (!host) return;
    host.innerHTML = '';
    metrics.forEach((metric) => host.appendChild(buildPoliticalOption(metric, () => ExploreMap.setColorBy(metric))));
  }

  /** Toggles de POI por categoria, na ordem de CAT_META (assets/explore.js 469-485). */
  private buildPoiToggles(): void {
    const host = qs<HTMLElement>('#expPoiToggles');
    if (!host) return;
    host.innerHTML = '';
    (Object.keys(CAT_META) as ExplorePoiCategory[]).forEach((cat) => {
      host.appendChild(buildPoiToggle(cat, (on2) => ExploreMap.togglePoi(cat, on2)));
    });
  }

  /** Toggles de marcadores de campanha (Lideranças/Doadores) (decisao de produto 2026-05-31). */
  private buildCampaignToggles(): void {
    const host = qs<HTMLElement>('#expCampanhaToggles');
    if (!host) return;
    host.innerHTML = '';
    (Object.keys(CAMPAIGN_MARKER_META) as ExploreCampaignMarkerKind[]).forEach((kind) => {
      host.appendChild(buildCampaignToggle(kind, (on2) => ExploreMap.toggleCampaign(kind, on2)));
    });
  }

  /** Toggle "Zonas eleitorais (TSE)"; se o asset falhar, desliga o switch e avisa. */
  private buildTseZoneToggleRow(): void {
    const host = qs<HTMLElement>('#expLimitesToggles');
    if (!host) return;
    host.innerHTML = '';
    const row = buildTseZoneToggle((on2) => {
      void ExploreMap.toggleTseZones(on2).then((ok) => {
        if (ok) return;
        resetToggleRow(row);
        showMiniHint('Não foi possível carregar as zonas eleitorais');
      });
    });
    host.appendChild(row);
    host.appendChild(this.buildG26LocaisRow());
  }

  /** Toggle "Locais de votação 2026"; se o asset falhar, desliga o switch e avisa. */
  private buildG26LocaisRow(): HTMLElement {
    const row = buildG26LocaisToggle((on2) => {
      void ExploreMap.toggleG26Locais(on2).then((ok) => {
        if (ok) return;
        resetToggleRow(row);
        showMiniHint('Não foi possível carregar os locais de votação');
      });
    });
    return row;
  }

  /** Toggle "Agenda em campo" (assets/explore.js 486-491). */
  private wireAcoesToggle(): void {
    const toggle = qs<HTMLElement>('#expAcoesToggle');
    if (!toggle) return;
    on(toggle, 'click', () => {
      const on2 = toggle.getAttribute('aria-checked') !== 'true';
      toggle.setAttribute('aria-checked', on2 ? 'true' : 'false');
      toggle.classList.toggle('is-on', on2);
      ExploreMap.toggleAcoes(on2);
    });
  }

  /** Abre/fecha o painel de Camadas (assets/explore.js 493-502). */
  private wireLayersButton(): void {
    const button = qs<HTMLElement>('#expLayersBtn');
    const panel = qs<HTMLElement>('#expLayersPanel');
    if (!button || !panel) return;
    on(button, 'click', (ev) => {
      ev.stopPropagation();
      const opening = panel.hasAttribute('hidden');
      panel.toggleAttribute('hidden', !opening);
      button.setAttribute('aria-expanded', opening ? 'true' : 'false');
      button.classList.toggle('is-open', opening);
    });
    on(panel, 'click', (ev) => ev.stopPropagation());
  }

  // ── busca ────────────────────────────────────────────────────────────────────

  /**
   * Liga o input de busca: e gatilho da command palette global (assets/explore.js
   * 503-533). Sem palette, faz fallback de busca inline por Enter (municipio/zona).
   */
  private wireSearch(): void {
    const input = qs<HTMLInputElement>('#expSearch');
    if (!input) return;
    const openPalette = (ev?: Event): boolean => this.openSearchPalette(input, ev);
    on(input, 'mousedown', openPalette);
    on(input, 'focus', openPalette);
    on(input, 'keydown', (ev) => this.onSearchKeydown(input, ev, openPalette));
  }

  /** Abre a command palette global (se presente) e tira o foco do input. */
  private openSearchPalette(input: HTMLInputElement, ev?: Event): boolean {
    const palette = getSearchPalette();
    if (!palette) return false;
    ev?.preventDefault();
    input.blur();
    palette.open();
    return true;
  }

  /** Trata Enter/Espaco no input: palette quando houver, senao busca inline. */
  private onSearchKeydown(input: HTMLInputElement, ev: KeyboardEvent, openPalette: (e?: Event) => boolean): void {
    if ((ev.key === 'Enter' || ev.key === ' ') && openPalette(ev)) return;
    if (ev.key !== 'Enter' || getSearchPalette()) return;
    this.runInlineSearch(input.value.trim());
  }

  /** Busca inline (fallback): municipio -> zona -> MiniHint (assets/explore.js 521-532). */
  private runInlineSearch(rawQuery: string): void {
    const query = rawQuery.toLowerCase();
    if (!query) { ExploreMap.setRegion(null); return; }
    const munId = this.findMunByName(query);
    if (munId) { ExploreMap.flyToMun(munId); ExploreMap.select('mun', munId, null); this.showCard('mun', munId); return; }
    if (this.matchesZone(query)) { ExploreMap.flyToMun('1302603'); return; }
    showMiniHint(`Sem resultados para “${rawQuery}”`);
  }

  /** Primeiro municipio cujo nome contem a busca (assets/explore.js 524-525). */
  private findMunByName(query: string): MunicipalityId | null {
    const hit = Object.values(EXPLORE_DATASET.munis).find((m) => m.name.toLowerCase().includes(query));
    return hit ? hit.id : null;
  }

  /** True quando a busca casa o nome de exibicao de alguma zona (assets/explore.js 528-529). */
  private matchesZone(query: string): boolean {
    return EXPLORE_ZONES.some((key) => (MANAUS_ZONA_DISPLAY_NAME[key] ?? key).toLowerCase().includes(query));
  }

  // ── card de drill-in ─────────────────────────────────────────────────────────

  /** Liga as acoes do card (fechar / abrir detalhe) (assets/explore.js 534-541). */
  private wireCard(): void {
    const card = qs<HTMLElement>('#expCard');
    if (!card) return;
    on(card, 'click', (ev) => this.onCardClick(ev));
  }

  /** Dispatcher de clique no card por data-card-action. */
  private onCardClick(ev: MouseEvent): void {
    const action = (ev.target as HTMLElement).closest<HTMLElement>('[data-card-action]');
    const cargoTab = (ev.target as HTMLElement).closest<HTMLElement>('[data-g26-cargo]');
    if (cargoTab) { this.switchCardCargo(cargoTab.dataset.g26Cargo as G26CargoCode); return; }
    if (!action) return;
    if (action.dataset.cardAction === 'close') { this.hideCard(); ExploreMap.clearSelection(); }
    else if (action.dataset.cardAction === 'detail') this.openDetail();
  }

  /** Aba de cargo no card 2026: troca o cargo do mapa e remonta o card do municipio. */
  private switchCardCargo(cargo: G26CargoCode): void {
    const munId = ExploreMap.selectedKind === 'mun' ? ExploreMap.selectedId : null;
    void this.enterG26(cargo).then((ok) => { if (ok && munId) this.showCard('mun', munId); });
  }

  /** Drill-in: fecha o card/rota e foca a zona/municipio no mapa principal (assets/explore.js 542-552). */
  private openDetail(): void {
    const kind = ExploreMap.selectedKind;
    const id = ExploreMap.selectedId;
    this.hideCard();
    const topbar = getTopbarNav();
    if (topbar) topbar.go('mapa');
    else getExploreRouteGlobal()?.close();
    setTimeout(() => this.focusMainMap(kind, id), DRILL_IN_DELAY_MS);
  }

  /** Aciona focusZone/focusMun no MapController principal apos o drill-in. */
  private focusMainMap(kind: ExploreSelectionKind | null, id: string | null): void {
    const controller = getMapController();
    if (!controller || !id) return;
    if (kind === 'zone') controller.focusZone?.(id as ZonaKey);
    else if (kind === 'mun') controller.focusMun?.(id);
  }

  /** Mostra o card do municipio/zona selecionado (ExploreMapUiPort). */
  showCard(kind: ExploreSelectionKind, id: string): void {
    const card = qs<HTMLElement>('#expCard');
    if (!card) return;
    const g26 = ExploreMap.g26.selection();
    if (g26 && kind === 'mun') card.innerHTML = buildG26MunCardHtml(g26, id, ExploreMap.munName(id), ExploreMap.candidate);
    else card.innerHTML = kind === 'mun'
      ? buildMunCardHtml(id, ExploreMap.year, ExploreMap.colorBy)
      : buildZoneCardHtml(id as ZonaKey, ExploreMap.year);
    card.removeAttribute('hidden');
  }

  /** Esconde o card de drill-in (ExploreMapUiPort). */
  hideCard(): void {
    qs<HTMLElement>('#expCard')?.setAttribute('hidden', '');
  }

  // ── dismiss global ───────────────────────────────────────────────────────────

  /** Liga o fechamento por clique fora e por Escape (assets/explore.js 600-612). */
  private wireGlobalDismiss(): void {
    document.addEventListener('click', () => this.closeMenu());
    document.addEventListener('keydown', (ev) => this.onGlobalKeydown(ev));
  }

  /** Escape: fecha menu -> painel -> card, nessa ordem, so com a rota aberta. */
  private onGlobalKeydown(ev: KeyboardEvent): void {
    if (ev.key !== 'Escape') return;
    if (!getExploreRouteGlobal()?.isOpen()) return;
    if (this.activeMenu) { this.closeMenu(); return; }
    const panel = qs<HTMLElement>('#expLayersPanel');
    if (panel && !panel.hasAttribute('hidden')) { qs<HTMLElement>('#expLayersBtn')?.click(); return; }
    const card = qs<HTMLElement>('#expCard');
    if (card && !card.hasAttribute('hidden')) { this.hideCard(); ExploreMap.clearSelection(); }
  }

  // ── refresh (chips + opcoes + legenda) ───────────────────────────────────────

  /** Sincroniza chips, opcoes politicas, checks de menu e legenda (ExploreMapUiPort). */
  refresh(): void {
    this.refreshDynamicMenus();
    this.refreshChips();
    this.refreshPoliticalOptions();
    this.refreshVoteVizControl();
    this.refreshMenuChecks();
    this.renderLegend();
  }

  /** Marca o modo ativo no segmentado e sinaliza overlay ativo no body (CSS do card). */
  private refreshVoteVizControl(): void {
    qsa<HTMLElement>('.exp-viz-btn').forEach((button) => {
      const on = button.dataset.viz === ExploreMap.voteViz;
      button.classList.toggle('is-on', on);
      button.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    document.body.classList.toggle('is-viz-active', ExploreMap.voteViz !== 'nenhum');
  }

  /** Rotulo do chip de candidato: nome do selecionado, ou "Candidato". */
  private candidateChipLabel(): string {
    if (!ExploreMap.candidate) return 'Candidato';
    const g26 = ExploreMap.g26.selection();
    const name = g26 ? g26CandidateChipLabel(g26.ds, ExploreMap.candidate) : findCandidate(ExploreMap.candidate)?.nome;
    return name ?? 'Candidato';
  }

  /** True quando colorBy e um dos eixos tematicos (chip "Tema" ativo). */
  private isThemeActive(): boolean {
    return (THEME_METRICS as readonly string[]).includes(ExploreMap.colorBy);
  }

  /** Atualiza o texto e o estado ativo dos chips (assets/explore.js 615-622). */
  private refreshChips(): void {
    const themeActive = this.isThemeActive();
    const g26 = ExploreMap.g26.selection();
    setChipText(qs<HTMLElement>('#expChipAno'), g26 ? G26_YEAR_LABEL : electionYearLabel(ExploreMap.year));
    const cargoWrap = qs<HTMLElement>('#expCargoWrap');
    if (cargoWrap) cargoWrap.hidden = !g26;
    if (g26) setChipText(qs<HTMLElement>('#expChipCargo'), g26CargoChipLabel(g26));
    setChipText(qs<HTMLElement>('#expChipPartido'), ExploreMap.party ?? 'Partido');
    setChipText(qs<HTMLElement>('#expChipRegiao'), ExploreMap.region ? ExploreMap.region.replace(' Amazonense', '') : 'Região');
    setChipText(qs<HTMLElement>('#expChipTema'), themeActive ? (METRIC_META[ExploreMap.colorBy]?.label ?? 'Tema') : 'Tema');
    setChipText(qs<HTMLElement>('#expChipCandidato'), this.candidateChipLabel());
    toggleChipActive(qs<HTMLElement>('#expChipPartido'), !!ExploreMap.party);
    toggleChipActive(qs<HTMLElement>('#expChipRegiao'), !!ExploreMap.region);
    toggleChipActive(qs<HTMLElement>('#expChipTema'), themeActive);
    toggleChipActive(qs<HTMLElement>('#expChipCandidato'), !!ExploreMap.candidate);
    this.refreshRecorteChips();
  }

  /** Texto/estado dos 3 chips de recorte (decisao de produto 2026-05-31). */
  private refreshRecorteChips(): void {
    setChipText(qs<HTMLElement>('#expChipMicro'), ExploreMap.micro ?? 'Microrregião');
    setChipText(qs<HTMLElement>('#expChipEleitorado'), eleitoradoLabel(ExploreMap.eleitoradoBand) ?? 'Eleitorado');
    setChipText(qs<HTMLElement>('#expChipSegundo'), ExploreMap.runnerUp ?? '2º colocado');
    toggleChipActive(qs<HTMLElement>('#expChipMicro'), !!ExploreMap.micro);
    toggleChipActive(qs<HTMLElement>('#expChipEleitorado'), !!ExploreMap.eleitoradoBand);
    toggleChipActive(qs<HTMLElement>('#expChipSegundo'), !!ExploreMap.runnerUp);
  }

  /** Marca a opcao de cor ativa nos 3 grupos do painel (assets/explore.js 624-628). */
  private refreshPoliticalOptions(): void {
    qsa<HTMLElement>('.exp-pol-opt').forEach((button) => {
      const on2 = button.dataset.metric === ExploreMap.colorBy;
      button.classList.toggle('is-on', on2);
      button.setAttribute('aria-checked', on2 ? 'true' : 'false');
    });
  }

  /** Marca o item escolhido em cada menu (assets/explore.js 630-633). */
  private refreshMenuChecks(): void {
    const themeActive = this.isThemeActive();
    const g26 = ExploreMap.g26.selection();
    markMenuChecked(qs<HTMLElement>('#expMenuAno'), g26 ? G26_YEAR_VALUE : ExploreMap.year);
    markMenuChecked(qs<HTMLElement>('#expMenuCargo'), g26?.cargo ?? '');
    markMenuChecked(qs<HTMLElement>('#expMenuPartido'), ExploreMap.party ?? '');
    markMenuChecked(qs<HTMLElement>('#expMenuRegiao'), ExploreMap.region ?? '');
    markMenuChecked(qs<HTMLElement>('#expMenuTema'), themeActive ? ExploreMap.colorBy : '');
    markMenuChecked(qs<HTMLElement>('#expMenuMicro'), ExploreMap.micro ?? '');
    markMenuChecked(qs<HTMLElement>('#expMenuEleitorado'), ExploreMap.eleitoradoBand ?? '');
    markMenuChecked(qs<HTMLElement>('#expMenuSegundo'), ExploreMap.runnerUp ?? '');
    markMenuChecked(qs<HTMLElement>('#expMenuCandidato'), ExploreMap.candidate ?? '');
  }

  /** Renderiza a legenda da metrica ativa, ou a esconde se vazia (assets/explore.js 644-652). */
  private renderLegend(): void {
    const box = qs<HTMLElement>('#expLegend');
    if (!box) return;
    const legend = ExploreMap.getLegend();
    if (!legend.items.length) { box.setAttribute('hidden', ''); return; }
    box.removeAttribute('hidden');
    const rows = legend.items
      .map((item) => `<div class="exp-legend-item"><span class="exp-legend-sw" style="background:${item[1]}"></span>${item[0]}</div>`)
      .join('');
    box.innerHTML = `<div class="exp-legend-title">${legend.title}</div>${rows}`;
  }
}

/** Singleton da UI da tela Explorar — substitui o `var ExploreUI` legado. */
export const ExploreUI = new ExploreScreenUi();

/** Liga o mapa a UI (DI) — chamado uma vez no boot da feature. */
export function bindExploreUi(): void {
  ExploreMap.bindUi(ExploreUI);
}
